// Decode image formats most browsers cannot open (only Safari reads TIFF and HEIC):
// TIFF with utif2 (uncompressed, LZW, Deflate, PackBits, JPEG-in-TIFF, either byte order) and
// HEIC/HEIF with heic-to (libheif compiled to WebAssembly, LGPL, loaded as its own chunk).
// Both are imported on demand so tools that never see these files do not ship them.
//
// @cross/image is not used for decoding: in Chrome it hands TIFF and HEIC to the browser's
// ImageDecoder, which supports neither, so every non-Safari visitor got an error (2026-10-04).

export const SOFTWARE_DECODED = /\.(tiff?|hei[cf])$/i;

export const SOFTWARE_DECODED_ACCEPT = '.tif,.tiff,.heic,.heif';

const isHeic = (file: File) => /\.hei[cf]$/i.test(file.name) || /image\/hei[cf]/.test(file.type);

function canvasFrom(width: number, height: number) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Your browser could not start image decoding.');
  return { canvas, context };
}

export async function decodeToCanvas(file: File): Promise<HTMLCanvasElement> {
  if (isHeic(file)) {
    const { heicTo } = await import('heic-to/next');
    const bitmap = await heicTo({ blob: file, type: 'bitmap' });
    const { canvas, context } = canvasFrom(bitmap.width, bitmap.height);
    context.drawImage(bitmap, 0, 0);
    bitmap.close();
    return canvas;
  }
  const UTIF = (await import('utif2')).default;
  const buffer = await file.arrayBuffer();
  const ifds = UTIF.decode(buffer);
  // Multi-page TIFFs: the first page. Thumbnails are stored as later, smaller IFDs.
  const page = ifds[0];
  if (!page) throw new Error('This TIFF contains no image.');
  UTIF.decodeImage(buffer, page);
  const rgba = UTIF.toRGBA8(page);
  const { canvas, context } = canvasFrom(page.width, page.height);
  context.putImageData(new ImageData(new Uint8ClampedArray(rgba), page.width, page.height), 0, 0);
  return canvas;
}

/** A PNG copy the browser can display, for pipelines built around <img> object URLs. */
export async function decodeToPngFile(file: File): Promise<File> {
  const canvas = await decodeToCanvas(file);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('This image could not be decoded.');
  return new File([blob], file.name.replace(/\.[^.]+$/, '.png'), { type: 'image/png' });
}

export const UNDECODABLE_MESSAGE =
  'This file could not be decoded. It may be damaged, or use a rare TIFF or HEIC variant; opening it in Preview or Photos and exporting again usually fixes it.';

/** Encode a canvas to a format canvas.toBlob cannot produce. */
export async function encodeCanvasInSoftware(
  canvas: HTMLCanvasElement,
  format: 'tiff' | 'bmp' | 'gif',
): Promise<Blob> {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Your browser could not start image encoding.');
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
  if (format === 'tiff') {
    // Uncompressed: utif2 writes it in milliseconds; @cross/image's LZW took ~30 s per page.
    const UTIF = (await import('utif2')).default;
    const bytes = UTIF.encodeImage(new Uint8Array(pixels.data.buffer), canvas.width, canvas.height);
    return new Blob([bytes], { type: 'image/tiff' });
  }
  const { Image: CrossImage } = await import('@cross/image');
  const image = CrossImage.fromRGBA(
    canvas.width,
    canvas.height,
    new Uint8Array(pixels.data.buffer),
  );
  const bytes = await image.encode(format);
  return new Blob([bytes as BlobPart], { type: `image/${format}` });
}
